import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  SafeAreaView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type RootStackParamList from "../navigation/RootStackParamList";
import {
  AdminUserEmail,
  AdminUserSort,
  sortAdminUsers,
  getUserEmails,
  toggleUserBan,
  deleteUser,
  getFlagsCountMap,
  getAllFlagReports,
  FlagReport,
} from "../services/adminUsersService";
import { useAppSelector } from "../redux/store";
import { formatClientInfo, type StoredClientInfo } from "../utils/clientInfo";

function formatJoinDate(createdAt: number | null): string {
  if (createdAt == null) return "Joined date unknown";
  return `Joined ${new Date(createdAt).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
}

function formatClientLine(client: StoredClientInfo): string {
  const seen = client.updatedAt
    ? ` · seen ${new Date(client.updatedAt).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })}`
    : "";
  return `${formatClientInfo(client)}${seen}`;
}

const NARROW_ROW_BREAKPOINT = 600;

export default function AdminUsersScreen() {
  // On phones the action buttons leave the email column ~20dp wide, so the
  // address wraps one character per line — move them to their own row there.
  const { width } = useWindowDimensions();
  const isNarrow = width < NARROW_ROW_BREAKPOINT;
  const isAdmin = useAppSelector((state) => state.user.admin);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [users, setUsers] = useState<AdminUserEmail[]>([]);
  const [flagsCountMap, setFlagsCountMap] = useState<Record<string, number>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isUpdatingBan, setIsUpdatingBan] = useState<string | null>(null);
  // User id pending delete confirmation, and the one currently being deleted.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  const [isDeletingUser, setIsDeletingUser] = useState<string | null>(null);
  const [allReports, setAllReports] = useState<FlagReport[]>([]);
  const [expandedUserIds, setExpandedUserIds] = useState<
    Record<string, boolean>
  >({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<
    "all" | "flagged" | "banned" | "admins"
  >("all");
  const [sortMode, setSortMode] = useState<AdminUserSort>("joined_desc");

  const toggleExpandUser = (userId: string) => {
    setExpandedUserIds((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const filteredUsers = useMemo(() => {
    let result = users;

    // Text search
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.name && u.name.toLowerCase().includes(q)),
      );
    }

    // Status filter
    switch (activeFilter) {
      case "flagged":
        result = result.filter((u) => (flagsCountMap[u.id] || 0) > 0);
        break;
      case "banned":
        result = result.filter((u) => u.banned);
        break;
      case "admins":
        result = result.filter((u) => u.admin);
        break;
    }

    return sortAdminUsers(result, sortMode);
  }, [users, searchQuery, activeFilter, flagsCountMap, sortMode]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    setErrorText(null);
    try {
      const nextUsers = await getUserEmails();
      const flagsMap = await getFlagsCountMap();
      const reports = await getAllFlagReports();
      setUsers(nextUsers);
      setFlagsCountMap(flagsMap);
      setAllReports(reports);
    } catch (error) {
      console.error("Failed to load user emails and flags:", error);
      setErrorText("Failed to load user emails.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const handleToggleBan = async (
    userId: string,
    currentBannedState: boolean,
  ) => {
    setIsUpdatingBan(userId);
    try {
      await toggleUserBan(userId, currentBannedState);
      await loadUsers();
    } catch (error) {
      console.error("Failed to toggle ban state:", error);
    } finally {
      setIsUpdatingBan(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setIsDeletingUser(userId);
    setErrorText(null);
    try {
      await deleteUser(userId);
      setConfirmingDeleteId(null);
      await loadUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      setErrorText("Failed to delete user.");
    } finally {
      setIsDeletingUser(null);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.screen}>
        <View style={styles.accessState}>
          <Ionicons name="lock-closed-outline" size={28} color="#3a6fb1" />
          <Text style={styles.title}>Access restricted</Text>
          <Text style={styles.subtitle}>
            This page is only available to administrators.
          </Text>
          <Pressable
            style={styles.primaryButton}
            onPress={() => navigation.navigate("MeetingsTabs")}
          >
            <Text style={styles.primaryButtonText}>Back to meetings</Text>
          </Pressable>
        </View>
      </SafeAreaView>
    );
  }

  const FILTERS: { key: typeof activeFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "flagged", label: "Flagged" },
    { key: "banned", label: "Banned" },
    { key: "admins", label: "Admins" },
  ];

  const SORTS: { key: AdminUserSort; label: string }[] = [
    { key: "joined_desc", label: "Newest" },
    { key: "joined_asc", label: "Oldest" },
    { key: "email", label: "A–Z" },
  ];

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Pressable
            style={styles.backButton}
            onPress={() =>
              navigation.canGoBack()
                ? navigation.goBack()
                : navigation.navigate("MeetingsTabs")
            }
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <Ionicons name="chevron-back" size={24} color="#3a6fb1" />
          </Pressable>
          <View>
            <Text style={styles.title}>User emails</Text>
            <Text style={styles.subtitle}>
              {filteredUsers.length} of {users.length} users
            </Text>
          </View>
        </View>
        <Pressable
          style={styles.refreshButton}
          onPress={loadUsers}
          accessibilityRole="button"
          accessibilityLabel="Refresh user emails"
        >
          <Ionicons name="refresh" size={18} color="#3a6fb1" />
        </Pressable>
      </View>

      <View style={styles.searchContainer}>
        <Ionicons
          name="search"
          size={18}
          color="rgba(43,43,47,0.4)"
          style={styles.searchIcon}
        />
        <TextInput
          style={styles.searchInput}
          placeholder="Search by email or name…"
          placeholderTextColor="rgba(43,43,47,0.4)"
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchQuery.length > 0 && (
          <Pressable
            onPress={() => setSearchQuery("")}
            style={styles.clearButton}
          >
            <Ionicons
              name="close-circle"
              size={18}
              color="rgba(43,43,47,0.4)"
            />
          </Pressable>
        )}
      </View>

      <View style={styles.filterRow}>
        {FILTERS.map((f) => (
          <Pressable
            key={f.key}
            style={[
              styles.filterChip,
              activeFilter === f.key && styles.filterChipActive,
            ]}
            onPress={() => setActiveFilter(f.key)}
          >
            <Text
              style={[
                styles.filterChipText,
                activeFilter === f.key && styles.filterChipTextActive,
              ]}
            >
              {f.label}
            </Text>
          </Pressable>
        ))}
      </View>

      <View style={styles.sortRow}>
        <Text style={styles.sortLabel}>Sort by</Text>
        {SORTS.map((s) => (
          <Pressable
            key={s.key}
            style={[
              styles.filterChip,
              sortMode === s.key && styles.filterChipActive,
            ]}
            onPress={() => setSortMode(s.key)}
            accessibilityRole="button"
            accessibilityLabel={`Sort by ${s.label}`}
          >
            <Text
              style={[
                styles.filterChipText,
                sortMode === s.key && styles.filterChipTextActive,
              ]}
            >
              {s.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {loading ? (
        <ActivityIndicator size="large" style={styles.loader} />
      ) : (
        <FlatList
          data={filteredUsers}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={
            <Text style={styles.emptyText}>No user emails found.</Text>
          }
          renderItem={({ item }) => {
            const userReports = allReports.filter(
              (r) => r.flaggedUserId === item.id,
            );
            return (
              <View style={styles.userCard}>
                <View
                  style={[
                    styles.userRowInner,
                    isNarrow && styles.userRowInnerNarrow,
                  ]}
                >
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>
                      {item.email.charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={styles.userDetails}>
                    <Text style={styles.emailText}>{item.email}</Text>
                    {!!item.name && (
                      <Text style={styles.nameText}>{item.name}</Text>
                    )}
                    <Text style={styles.joinDateText}>
                      {formatJoinDate(item.createdAt)}
                    </Text>
                    {item.clients.map((client) => (
                      <Text
                        key={client.platform}
                        style={styles.joinDateText}
                        selectable
                      >
                        {formatClientLine(client)}
                      </Text>
                    ))}
                    {(flagsCountMap[item.id] || 0) > 0 && (
                      <Pressable onPress={() => toggleExpandUser(item.id)}>
                        <Text style={styles.flagCountLink}>
                          🚩 {flagsCountMap[item.id]}{" "}
                          {flagsCountMap[item.id] === 1 ? "report" : "reports"}{" "}
                          (
                          {expandedUserIds[item.id]
                            ? "hide details"
                            : "view details"}
                          )
                        </Text>
                      </Pressable>
                    )}
                  </View>
                  <View
                    style={[
                      styles.userActions,
                      isNarrow && styles.userActionsNarrow,
                    ]}
                  >
                    <Pressable
                      style={styles.viewConversationsButton}
                      onPress={() =>
                        navigation.navigate("AdminConversations", {
                          initialSearch: item.email,
                        })
                      }
                      accessibilityRole="button"
                      accessibilityLabel={`View conversations for ${item.email}`}
                    >
                      <Ionicons
                        name="calendar-outline"
                        size={18}
                        color="#3a6fb1"
                      />
                    </Pressable>
                    {item.banned && !item.admin && (
                      <View style={styles.bannedBadge}>
                        <Text style={styles.bannedBadgeText}>Banned</Text>
                      </View>
                    )}
                    {item.admin && (
                      <View style={styles.adminBadge}>
                        <Text style={styles.adminBadgeText}>Admin</Text>
                      </View>
                    )}
                    {!item.admin && (
                      <Pressable
                        style={[
                          styles.banButton,
                          item.banned
                            ? styles.unbanButton
                            : styles.banButtonActive,
                          isUpdatingBan === item.id && styles.buttonDisabled,
                        ]}
                        onPress={() => handleToggleBan(item.id, item.banned)}
                        disabled={isUpdatingBan === item.id}
                      >
                        <Text
                          style={[
                            styles.banButtonText,
                            item.banned
                              ? styles.unbanButtonText
                              : styles.banButtonTextActive,
                          ]}
                        >
                          {isUpdatingBan === item.id
                            ? "..."
                            : item.banned
                              ? "Unban"
                              : "Ban"}
                        </Text>
                      </Pressable>
                    )}
                    <Pressable
                      style={styles.deleteIconButton}
                      onPress={() =>
                        setConfirmingDeleteId(
                          confirmingDeleteId === item.id ? null : item.id,
                        )
                      }
                      disabled={isDeletingUser === item.id}
                      accessibilityRole="button"
                      accessibilityLabel={`Delete ${item.email}`}
                    >
                      <Ionicons
                        name="trash-outline"
                        size={18}
                        color="#C62828"
                      />
                    </Pressable>
                  </View>
                </View>

                {confirmingDeleteId === item.id && (
                  <View style={styles.deleteConfirmBar}>
                    <Text style={styles.deleteConfirmText}>
                      Permanently delete {item.email} and all their data? This
                      cannot be undone.
                    </Text>
                    <View style={styles.deleteConfirmActions}>
                      <Pressable
                        style={styles.deleteCancelButton}
                        onPress={() => setConfirmingDeleteId(null)}
                        disabled={isDeletingUser === item.id}
                      >
                        <Text style={styles.deleteCancelText}>Cancel</Text>
                      </Pressable>
                      <Pressable
                        style={[
                          styles.deleteConfirmButton,
                          isDeletingUser === item.id && styles.buttonDisabled,
                        ]}
                        onPress={() => handleDeleteUser(item.id)}
                        disabled={isDeletingUser === item.id}
                      >
                        <Text style={styles.deleteConfirmButtonText}>
                          {isDeletingUser === item.id ? "Deleting…" : "Delete"}
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                )}

                {expandedUserIds[item.id] && userReports.length > 0 && (
                  <View style={styles.reportsSection}>
                    <Text style={styles.reportsSectionTitle}>
                      Flagged Reports ({userReports.length})
                    </Text>
                    {userReports.map((rep) => (
                      <View key={rep.id} style={styles.reportItem}>
                        <Text style={styles.reportText}>
                          <Text style={styles.bold}>Reason:</Text>{" "}
                          {rep.reason || "Unspecified"}
                        </Text>
                        <Text style={styles.reportText}>
                          <Text style={styles.bold}>Details:</Text>{" "}
                          {rep.details || "None"}
                        </Text>
                        <Text style={styles.reportMeta}>
                          By: {rep.flaggedByUserName || "Guest"} | Room:{" "}
                          {rep.roomName || "Unknown"}
                        </Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            );
          }}
        />
      )}

      {!!errorText && <Text style={styles.errorText}>{errorText}</Text>}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#F6F7F9",
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  header: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 24,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  title: {
    fontFamily: "Lexend",
    fontSize: 30,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  subtitle: {
    marginTop: 6,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "rgba(43, 43, 47, 0.62)",
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  searchContainer: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.1)",
    paddingHorizontal: 12,
    marginBottom: 12,
    height: 44,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontFamily: "Lexend",
    fontSize: 14,
    color: "#2B2B2F",
    outlineStyle: "none" as any,
  },
  clearButton: {
    padding: 4,
  },
  filterRow: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    gap: 8,
    marginBottom: 14,
  },
  sortRow: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
  },
  sortLabel: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "600",
    color: "rgba(43, 43, 47, 0.62)",
    marginRight: 2,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.1)",
  },
  filterChipActive: {
    backgroundColor: "#3a6fb1",
    borderColor: "#3a6fb1",
  },
  filterChipText: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "600",
    color: "rgba(43, 43, 47, 0.62)",
  },
  filterChipTextActive: {
    color: "#fff",
  },
  loader: {
    marginTop: 40,
  },
  listContent: {
    width: "100%",
    maxWidth: 900,
    alignSelf: "center",
    gap: 10,
    paddingBottom: 40,
  },
  userCard: {
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.08)",
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  userRowInner: {
    minHeight: 48,
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3a6fb1",
  },
  avatarText: {
    fontFamily: "Lexend",
    fontSize: 16,
    fontWeight: "700",
    color: "#fff",
  },
  userRowInnerNarrow: {
    flexWrap: "wrap",
    rowGap: 10,
  },
  userActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  userActionsNarrow: {
    width: "100%",
    justifyContent: "flex-end",
  },
  userDetails: {
    flex: 1,
    minWidth: 0,
  },
  emailText: {
    fontFamily: "Lexend",
    fontSize: 15,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  nameText: {
    marginTop: 3,
    fontFamily: "Lexend",
    fontSize: 13,
    color: "rgba(43, 43, 47, 0.62)",
  },
  joinDateText: {
    marginTop: 3,
    fontFamily: "Lexend",
    fontSize: 12,
    color: "rgba(43, 43, 47, 0.45)",
  },
  adminBadge: {
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: "rgba(58, 111, 177, 0.12)",
  },
  adminBadgeText: {
    fontFamily: "Lexend",
    fontSize: 11,
    fontWeight: "700",
    color: "#3a6fb1",
    textTransform: "uppercase",
  },
  emptyText: {
    marginTop: 40,
    textAlign: "center",
    fontFamily: "Lexend",
    fontSize: 15,
    color: "rgba(43, 43, 47, 0.62)",
  },
  errorText: {
    marginTop: 12,
    textAlign: "center",
    color: "#B42318",
    fontFamily: "Lexend",
  },
  accessState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  primaryButton: {
    marginTop: 22,
    borderRadius: 8,
    backgroundColor: "#3a6fb1",
    paddingHorizontal: 18,
    paddingVertical: 12,
  },
  primaryButtonText: {
    fontFamily: "Lexend",
    fontSize: 13,
    fontWeight: "700",
    color: "#fff",
  },
  flagCountLink: {
    marginTop: 4,
    fontFamily: "Lexend",
    fontSize: 12,
    color: "#ff4444",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  bannedBadge: {
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: "rgba(255, 68, 68, 0.12)",
    marginRight: 8,
  },
  bannedBadgeText: {
    fontFamily: "Lexend",
    fontSize: 11,
    fontWeight: "700",
    color: "#ff4444",
    textTransform: "uppercase",
  },
  viewConversationsButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(58, 111, 177, 0.18)",
  },
  banButton: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    minWidth: 70,
    alignItems: "center",
    marginLeft: 8,
  },
  banButtonActive: {
    borderColor: "#ff4444",
    backgroundColor: "transparent",
  },
  unbanButton: {
    borderColor: "#3a6fb1",
    backgroundColor: "transparent",
  },
  banButtonText: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "700",
  },
  banButtonTextActive: {
    color: "#ff4444",
  },
  unbanButtonText: {
    color: "#3a6fb1",
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  deleteIconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "rgba(198, 40, 40, 0.25)",
    marginLeft: 8,
  },
  deleteConfirmBar: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(198, 40, 40, 0.12)",
    paddingTop: 12,
    gap: 10,
  },
  deleteConfirmText: {
    fontFamily: "Lexend",
    fontSize: 13,
    color: "#C62828",
    lineHeight: 18,
  },
  deleteConfirmActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  deleteCancelButton: {
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "rgba(35, 35, 53, 0.15)",
    backgroundColor: "#fff",
  },
  deleteCancelText: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "700",
    color: "#2B2B2F",
  },
  deleteConfirmButton: {
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#C62828",
  },
  deleteConfirmButtonText: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "700",
    color: "#fff",
  },
  reportsSection: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: "rgba(0, 0, 0, 0.06)",
    paddingTop: 12,
    gap: 8,
  },
  reportsSectionTitle: {
    fontFamily: "Lexend",
    fontSize: 12,
    fontWeight: "700",
    color: "#ff4444",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  reportItem: {
    backgroundColor: "#fafafa",
    borderRadius: 6,
    padding: 8,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.04)",
  },
  reportText: {
    fontFamily: "Lexend",
    fontSize: 13,
    color: "#333",
    lineHeight: 18,
  },
  reportMeta: {
    fontFamily: "Lexend",
    fontSize: 11,
    color: "rgba(43, 43, 47, 0.5)",
    marginTop: 4,
  },
  bold: {
    fontWeight: "700",
  },
});
